import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PendingException } from 'src/common/types';
import classNames from 'classnames';
import { EXCEPTION_TYPES, PENDING_TYPES } from 'src/constants/exceptionConstants';
import { isDesktop, isMobile } from 'src/utils/devices';
import { Exception } from '../hosLog/types';
import {
  useConfirmRemoveExceptionHandler,
  useExceptionActionCallback,
  usePendingExceptionAction,
} from './hooks';

import { useDispatch, useSelector } from 'react-redux';
import { getExceptionMarkedToRemove } from './store/exceptionsListSelectors';
import { clearEditState } from '../store';
import { getHosLog } from 'src/features/hosLogs/store';

export interface Props {
  exceptions: Exception[];
  pendingExceptions: PendingException[];
  employeeId: number;
  logDate: string;
  onSaveEdits?: () => void;
}
export const ExceptionsList: React.FC<Props> = ({
  exceptions,
  pendingExceptions,
  employeeId,
  logDate,
  onSaveEdits,
}) => {
  const dispatch = useDispatch();

  const isMobileEditing = useMemo(() => isMobile, [isMobile]);

  const exceptionMarkedToRemove = useSelector(getExceptionMarkedToRemove);
  const { exemptStatus = false, certifiedStatus = 0 } = useSelector(getHosLog);

  const [hosLogDate, setHosLogdate] = useState(logDate);

  const exceptionsButtonStyle = classNames('log-info-remove-button', {
    'log-info-remove-button-mobile': isMobile,
  });

  const handleExceptionAction = useExceptionActionCallback();
  const handleRemovePendingException = usePendingExceptionAction();
  const handleConfirmRemoveException = useConfirmRemoveExceptionHandler(exceptionMarkedToRemove);

  useEffect(() => {
    if (logDate !== hosLogDate) {
      dispatch(clearEditState());
    }
    setHosLogdate(logDate);
  }, [dispatch, logDate, hosLogDate]);

  useEffect(() => {
    if (exceptionMarkedToRemove) {
      handleConfirmRemoveException();
      onSaveEdits && onSaveEdits();
    }
  }, [exceptionMarkedToRemove]);

  const exceptionActionButton = useCallback(
    (item: Exception): JSX.Element => (
      <div
        className={exceptionsButtonStyle}
        onClick={handleExceptionAction(item, employeeId, logDate)}
      >
        Remove
      </div>
    ),

    [exceptionsButtonStyle, handleExceptionAction, employeeId, logDate],
  );

  const renderExceptionActionButtonMobile = useCallback(
    (item: Exception) => isMobileEditing && exceptionActionButton(item),
    [isMobileEditing, exceptionActionButton],
  );

  const renderExceptionActionButtonDesktop = useCallback(
    (item: Exception) => {
      return <td data-title="Edit Status">{exceptionActionButton(item)}</td>;
    },
    [exceptionActionButton],
  );

  const removePendingExceptionButton = useCallback(
    (item: PendingException): JSX.Element => (
      <div className={exceptionsButtonStyle} onClick={handleRemovePendingException(item)}>
        Remove
      </div>
    ),
    [exceptionsButtonStyle, handleRemovePendingException],
  );

  const renderRemovePendingExceptionButtonMobile = useCallback(
    (item: PendingException) => {
      return isMobileEditing && removePendingExceptionButton(item);
    },
    [isMobileEditing, removePendingExceptionButton],
  );

  const renderRemovePendingExceptionButtonDesktop = useCallback(
    (item: PendingException) => {
      return <td>{removePendingExceptionButton(item)}</td>;
    },
    [removePendingExceptionButton],
  );

  return (
    <>
      <tbody className="log-info-tbody-content">
        {exceptions.map(item => (
          <tr
            key={item.exceptionId}
            className={classNames('log-info-box', { 'is-desktop': isDesktop })}
          >
            <td className="log-info-header log-info-header__exc">
              {EXCEPTION_TYPES[item.exceptionType].abbreviation}
              {!exemptStatus && !certifiedStatus && renderExceptionActionButtonMobile(item)}
            </td>

            <td colSpan={2} data-title="Exception">
              {EXCEPTION_TYPES[item.exceptionType].exception}
            </td>
            <td data-title="Location">{item.driverLocation ? item.driverLocation : '--'}</td>
            <td colSpan={2} data-title="Note">
              {item.notes ? item.notes : '--'}
            </td>

            <td />
            {!exemptStatus && !certifiedStatus && renderExceptionActionButtonDesktop(item)}
          </tr>
        ))}
        {pendingExceptions.map(item => (
          <tr
            key={item.exceptionTimestamp}
            className={classNames('log-info-box', { 'is-desktop': isDesktop })}
          >
            <td className={classNames('log-info-header', 'log-info-header__exc')}>
              {PENDING_TYPES[item.exceptionType].abbreviation}
              {renderRemovePendingExceptionButtonMobile(item)}
            </td>

            <td colSpan={2} data-title="Exception">
              {PENDING_TYPES[item.exceptionType].exception}
            </td>
            <td data-title="Location">{item.location ? item.location : '--'}</td>
            <td colSpan={2} data-title="Note">
              {item.comment ? item.comment : '--'}
            </td>

            <td data-title="timestamp">
              {item.exceptionTimestamp ? `Added ${item.exceptionTimestamp}` : '--'}
            </td>

            {!exemptStatus && !certifiedStatus && renderRemovePendingExceptionButtonDesktop(item)}
          </tr>
        ))}
      </tbody>
    </>
  );
};
